import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch, ArrowsClockwise, ShieldCheck, WarningCircle, CheckCircle, X, Plus, Trash } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABEL = { clinical: "Clinical", regulatory: "Regulatory", economic: "Economic", country_requirement: "Country requirement" };
const SOURCE_LABEL = {
  evidence_inventory: "Evidence Inventory", gvd: "GVD", dossier: "Dossier",
  country_profile: "Country Profile", readiness: "Readiness",
  executive_summary: "Executive Summary", recommendations: "Recommendations",
};
const STAGE_LABEL = {
  analyst_review: "Analyst Review", technical_review: "Technical Review",
  country_review: "Country Review", senior_approval: "Senior Approval",
  final_document_check: "Final Document Check",
};
const DECISION_TONE = {
  pending: "bg-slate-100 text-slate-600 border-slate-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  changes_requested: "bg-amber-50 text-amber-700 border-amber-300",
};
const STATUS_TONE = {
  open: "bg-red-50 text-red-700 border-red-200",
  under_review: "bg-amber-50 text-amber-700 border-amber-300",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  waived: "bg-slate-100 text-slate-600 border-slate-200",
};

function Pill({ tone, children, testId }) {
  return <span data-testid={testId} className={`inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${tone}`}>{children}</span>;
}

function FactsPanel({ productId, catalog }) {
  const [facts, setFacts] = useState([]);
  const [fieldFilter, setFieldFilter] = useState("all");
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({ field_id: "sample_size", source: "gvd", value: "", country_id: "", note: "" });
  const load = useCallback(() => {
    axios.get(`${API}/quality/facts`, { params: { product_id: productId } })
      .then((r) => setFacts(r.data || [])).catch(() => toast.error("Failed to load facts"));
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (form.value === "") return toast.error("Value is required");
    setBusy("save");
    try {
      const body = { ...form, country_id: form.country_id || null };
      // Coerce numeric-looking values
      if (form.value && !isNaN(Number(form.value))) body.value = Number(form.value);
      await axios.post(`${API}/quality/facts?product_id=${productId}`, body);
      toast.success("Fact recorded");
      setForm({ ...form, value: "", country_id: "", note: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(null); }
  };

  const rescan = async () => {
    setBusy("scan");
    try {
      const r = await axios.post(`${API}/quality/rescan?product_id=${productId}`);
      toast.success(`Auto-scanned ${r.data.scanned} · ${r.data.inserted} new`);
      load();
    } catch { toast.error("Rescan failed"); }
    finally { setBusy(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this fact?")) return;
    await axios.delete(`${API}/quality/facts/${id}`);
    load();
  };

  const filtered = fieldFilter === "all" ? facts : facts.filter((f) => f.field_id === fieldFilter);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 p-4 space-y-3">
        <p className="overline-label text-slate-400">Record a fact</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Select value={form.field_id} onValueChange={(v) => setForm({ ...form, field_id: v })}>
            <SelectTrigger data-testid="fact-field" className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              {catalog.fields.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
            <SelectTrigger data-testid="fact-source" className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {catalog.sources.map((s) => <SelectItem key={s} value={s}>{SOURCE_LABEL[s]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="fact-value" className="h-8 text-xs" placeholder="Value"
            value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
          <Input data-testid="fact-country" className="h-8 text-xs" placeholder="Country (optional)"
            value={form.country_id} onChange={(e) => setForm({ ...form, country_id: e.target.value })} />
        </div>
        <Textarea data-testid="fact-note" rows={1} className="text-xs" placeholder="Note (optional)"
          value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        <div className="flex items-center gap-2">
          <button data-testid="fact-save" onClick={submit} disabled={busy === "save"}
            className="text-[11px] font-semibold text-white bg-slate-900 px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
            {busy === "save" ? <CircleNotch size={11} className="animate-spin" /> : <Plus size={11} />} Save fact
          </button>
          <button data-testid="fact-rescan" onClick={rescan} disabled={busy === "scan"}
            className="text-[11px] font-semibold text-slate-700 border border-slate-300 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5">
            {busy === "scan" ? <CircleNotch size={11} className="animate-spin" /> : <ArrowsClockwise size={11} />} Auto-scan country + regulatory
          </button>
          <div className="ml-auto flex items-center gap-2">
            <span className="overline-label text-slate-400">Filter</span>
            <Select value={fieldFilter} onValueChange={setFieldFilter}>
              <SelectTrigger className="h-8 w-56 text-xs bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="all">All fields ({facts.length})</SelectItem>
                {catalog.fields.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div data-testid="facts-empty" className="py-16 text-center text-xs text-slate-400 border border-dashed border-slate-200">
          No facts recorded yet — enter one above or run auto-scan.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs" data-testid="facts-table">
            <thead className="bg-slate-50/60 text-[10px] uppercase tracking-widest text-slate-400">
              <tr>
                <th className="text-left px-3 py-2">Field</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Country</th>
                <th className="text-left px-3 py-2">Value</th>
                <th className="text-left px-3 py-2">Asserted by</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((f) => (
                <tr key={f.id} data-testid={`fact-row-${f.id}`} className="hover:bg-slate-50/50">
                  <td className="px-3 py-2 font-semibold text-slate-800">{catalog.fields.find((x) => x.id === f.field_id)?.label || f.field_id}</td>
                  <td className="px-3 py-2 text-slate-600">{SOURCE_LABEL[f.source]}</td>
                  <td className="px-3 py-2 text-slate-500">{f.country_id || "—"}</td>
                  <td className="px-3 py-2 font-mono-data">{String(f.value)}{f.unit ? ` ${f.unit}` : ""}</td>
                  <td className="px-3 py-2 text-slate-400 text-[10px]">{f.asserted_by || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button data-testid={`fact-del-${f.id}`} onClick={() => del(f.id)} className="text-slate-300 hover:text-red-600 p-1"><Trash size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ConflictsPanel({ productId, catalog }) {
  const [conflicts, setConflicts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("resolved");
  const load = useCallback(() => {
    axios.get(`${API}/quality/conflicts`, { params: { product_id: productId } })
      .then((r) => setConflicts(r.data || [])).catch(() => toast.error("Load failed"));
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/quality/conflicts/refresh?product_id=${productId}`);
      toast.success(`${r.data.count} conflicts detected`);
      load();
    } catch { toast.error("Refresh failed"); }
    finally { setBusy(false); }
  };

  const submitResolve = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/quality/conflicts/${resolveTarget.id}/resolve`, { status, note });
      toast.success("Conflict updated");
      setResolveTarget(null); setNote(""); setStatus("resolved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button data-testid="conflicts-refresh" onClick={refresh} disabled={busy}
          className="text-[11px] font-semibold text-white bg-slate-900 px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
          {busy ? <CircleNotch size={11} className="animate-spin" /> : <ArrowsClockwise size={11} />} Re-detect conflicts
        </button>
        <p className="text-[11px] text-slate-500">Compares the same fact across sources and lists any mismatch.</p>
      </div>
      {conflicts.length === 0 ? (
        <div data-testid="conflicts-empty" className="py-16 text-center text-xs text-slate-400 border border-dashed border-slate-200">
          No conflicts detected — record at least two facts for the same field from different sources, then re-detect.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {conflicts.map((c) => (
            <div key={c.id} data-testid={`conflict-${c.id}`} className="bg-white border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="overline-label text-slate-400">{CATEGORY_LABEL[c.category]}{c.country_id ? ` · ${c.country_id}` : ""}</p>
                  <p className="font-heading font-bold text-sm">{c.field_label}</p>
                </div>
                <Pill tone={STATUS_TONE[c.resolution_status]} testId={`conflict-status-${c.id}`}>
                  {c.resolution_status.replace(/_/g, " ")}
                </Pill>
              </div>
              <ul className="mt-2 space-y-1 text-[11px]">
                {c.source_values.map((v, i) => (
                  <li key={i} className="flex items-center gap-2 border border-slate-100 bg-slate-50/60 px-2 py-1.5">
                    <span className="font-semibold text-slate-500 w-40 truncate">{SOURCE_LABEL[v.source]}</span>
                    <code className="text-slate-800 font-mono-data">{String(v.value)}</code>
                    {v.asserted_by && <span className="text-[10px] text-slate-400 ml-auto">by {v.asserted_by}</span>}
                  </li>
                ))}
              </ul>
              {c.notes && (
                <p className="text-[11px] text-slate-500 mt-2 border-l-2 border-slate-300 pl-2">{c.notes}</p>
              )}
              <div className="mt-2 flex justify-end">
                <button data-testid={`conflict-resolve-${c.id}`} onClick={() => setResolveTarget(c)}
                  className="text-[11px] font-semibold text-slate-700 border border-slate-300 px-2.5 py-1.5 hover:bg-slate-50">Update</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {resolveTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setResolveTarget(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-slate-200 max-w-md w-full p-5 space-y-3" data-testid="resolve-dialog">
            <p className="font-heading font-bold text-sm">Update conflict</p>
            <p className="text-xs text-slate-500">{resolveTarget.field_label}{resolveTarget.country_id ? ` · ${resolveTarget.country_id}` : ""}</p>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="resolve-status" className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {["open", "under_review", "resolved", "waived"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea data-testid="resolve-note" rows={3} className="text-xs" placeholder="Reviewer note (recorded on audit)"
              value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setResolveTarget(null)} className="text-[11px] font-semibold text-slate-600 border border-slate-300 px-3 py-1.5 hover:bg-slate-50">Cancel</button>
              <button data-testid="resolve-submit" onClick={submitResolve} disabled={busy}
                className="text-[11px] font-semibold text-white bg-slate-900 px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewPanel({ productId, catalog }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [review, setReview] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(null);
  const [drafts, setDrafts] = useState({});
  const load = useCallback(async () => {
    const [r, a] = await Promise.all([
      axios.get(`${API}/quality/review`, { params: { product_id: productId } }),
      axios.get(`${API}/quality/review/audit`, { params: { product_id: productId } }),
    ]);
    setReview(r.data); setAudit(a.data || []);
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  const decide = async (stage, decision) => {
    setBusy(stage);
    try {
      await axios.post(`${API}/quality/review/${stage}?product_id=${productId}`, {
        decision, comment: drafts[stage] || "",
      });
      toast.success(`${STAGE_LABEL[stage]} recorded`);
      setDrafts({ ...drafts, [stage]: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(null); }
  };

  if (!review) return <p className="py-16 text-center text-xs text-slate-400">Loading review dossier…</p>;

  const stages = catalog.stages;
  const canApproveStage = (s) => {
    if (s === "senior_approval") {
      return ["analyst_review", "technical_review", "country_review"].every(
        (x) => review.stages[x].decision === "approved") && isAdmin;
    }
    if (s === "final_document_check") {
      return review.stages.senior_approval.decision === "approved" && isAdmin;
    }
    return true;
  };

  return (
    <div className="space-y-4">
      <div data-testid="review-gate" className={`px-5 py-4 border ${review.gate_passed ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"} flex items-center gap-3`}>
        {review.gate_passed
          ? <><CheckCircle size={18} weight="fill" className="text-emerald-700" />
            <p className="text-sm font-semibold text-emerald-800">All five reviews approved — this product is cleared for external use.</p></>
          : <><WarningCircle size={18} weight="fill" className="text-slate-400" />
            <p className="text-sm font-semibold text-slate-700">Review dossier in progress. All five stages must be Approved before external release.</p></>}
      </div>

      <div className="space-y-2">
        {stages.map((s, i) => {
          const stg = review.stages[s];
          return (
            <div key={s} data-testid={`stage-${s}`} className="bg-white border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <span className="font-mono-data text-[10px] text-slate-300 mt-1">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <p className="font-heading font-bold text-sm">{STAGE_LABEL[s]}</p>
                    {stg.reviewer_email && <p className="text-[11px] text-slate-500 mt-0.5">by {stg.reviewer_email}{stg.decided_at && <span className="text-slate-400"> · {new Date(stg.decided_at).toLocaleString()}</span>}</p>}
                    {stg.comment && <p className="text-[11px] text-slate-500 mt-1 border-l-2 border-slate-300 pl-2">{stg.comment}</p>}
                  </div>
                </div>
                <Pill tone={DECISION_TONE[stg.decision]} testId={`stage-status-${s}`}>{stg.decision.replace(/_/g, " ")}</Pill>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <Textarea data-testid={`comment-${s}`} rows={1} className="text-xs flex-1 min-w-[240px]"
                  placeholder="Reviewer comment"
                  value={drafts[s] || ""} onChange={(e) => setDrafts({ ...drafts, [s]: e.target.value })} />
                <button data-testid={`approve-${s}`} disabled={!canApproveStage(s) || busy === s}
                  onClick={() => decide(s, "approved")}
                  className="text-[11px] font-semibold text-white bg-emerald-700 hover:bg-emerald-800 px-3 py-1.5 disabled:opacity-40">
                  Approve
                </button>
                <button data-testid={`request-changes-${s}`} disabled={busy === s} onClick={() => decide(s, "changes_requested")}
                  className="text-[11px] font-semibold text-amber-700 border border-amber-300 hover:bg-amber-50 px-3 py-1.5 disabled:opacity-50">
                  Request changes
                </button>
                {(s === "senior_approval" || s === "final_document_check") ? (
                  <button data-testid={`reject-${s}`} disabled={!isAdmin || busy === s} onClick={() => decide(s, "rejected")}
                    className="text-[11px] font-semibold text-red-700 border border-red-300 hover:bg-red-50 px-3 py-1.5 disabled:opacity-40">
                    Reject
                  </button>
                ) : (
                  <button data-testid={`reject-${s}`} disabled={busy === s} onClick={() => decide(s, "rejected")}
                    className="text-[11px] font-semibold text-red-700 border border-red-300 hover:bg-red-50 px-3 py-1.5 disabled:opacity-50">
                    Reject
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {audit.length > 0 && (
        <div className="bg-white border border-slate-200 p-4" data-testid="review-audit">
          <p className="overline-label text-slate-400">Audit log</p>
          <ul className="space-y-1 mt-2 text-[11px] text-slate-600">
            {audit.slice(0, 40).map((a) => (
              <li key={a.id} className="border-l-2 border-slate-200 pl-2">
                <b>{a.actor_email}</b> · <span className="text-slate-500">{STAGE_LABEL[a.stage]}</span>
                {" → "}<span className="font-semibold">{a.decision}</span>
                <span className="text-slate-400"> · {new Date(a.at).toLocaleString()}</span>
                {a.comment && <p className="text-slate-500">{a.comment}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function QualityPage({ embedded }) {
  const { productId } = useProduct();
  const [tab, setTab] = useState("facts");
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    axios.get(`${API}/quality/catalog`).then((r) => setCatalog(r.data)).catch(() => toast.error("Catalog load failed"));
  }, []);

  return (
    <PageShell embedded={embedded} movedTo={{ label: "Evidence Review", to: "/console/evidence-review?tab=conflicts" }} pageId="quality" overline="Data Check & Quality Review · Prompt 11" title="Quality Review Workspace">
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[["facts", "Facts"], ["conflicts", "Conflict queue"], ["review", "5-stage review"]].map(([id, label]) => (
          <button key={id} data-testid={`quality-tab-${id}`} onClick={() => setTab(id)}
            className={`text-xs font-semibold px-4 py-2.5 border-b-2 -mb-px transition-colors ${tab === id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            {label}
          </button>
        ))}
      </div>
      {!catalog ? (
        <p data-testid="catalog-loading" className="py-16 text-center text-xs text-slate-400">Loading catalog…</p>
      ) : (
        <>
          {tab === "facts" && <FactsPanel productId={productId} catalog={catalog} />}
          {tab === "conflicts" && <ConflictsPanel productId={productId} catalog={catalog} />}
          {tab === "review" && <ReviewPanel productId={productId} catalog={catalog} />}
        </>
      )}
    </PageShell>
  );
}
